import React, { useState, useEffect } from 'react';
import { ui } from '../../../config/uiSizes';

interface OverwritePropertyParametersProps {
  parameters?: Record<string, unknown>;
  onParametersChange?: (parameters: Record<string, unknown>) => void;
}

export const OverwritePropertyParameters: React.FC<OverwritePropertyParametersProps> = ({
  parameters,
  onParametersChange
}) => {
  const [propertyName, setPropertyName] = useState<string>(
    (parameters?.['propertyName'] as string) || ''
  );
  const [newValue, setNewValue] = useState<string>((parameters?.['newValue'] as string) || '');
  const [createIfMissing, setCreateIfMissing] = useState<boolean>(
    parameters?.['createIfMissing'] !== undefined
      ? (parameters['createIfMissing'] as boolean)
      : true
  );
  const [expectedType, setExpectedType] = useState<string>(
    (parameters?.['expectedType'] as string) || ''
  );
  const [expectedFormat, setExpectedFormat] = useState<string>(
    (parameters?.['expectedFormat'] as string) || ''
  );

  useEffect(() => {
    if (onParametersChange) {
      const params: Record<string, unknown> = {
        propertyName,
        newValue,
        createIfMissing
      };

      if (expectedType) {
        params['expectedType'] = expectedType;
      }
      if (expectedFormat) {
        params['expectedFormat'] = expectedFormat;
      }

      onParametersChange(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyName, newValue, createIfMissing, expectedType, expectedFormat]);

  return (
    <div className={`border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 ${ui.page.cardPadding}`}>
      <div className={`${ui.mb.title}`}>
        <h3 className={`${ui.text.sectionTitle} font-bold text-gray-900 dark:text-gray-100`}>Overwrite Property</h3>
        <p className={`${ui.text.label} text-gray-600 dark:text-gray-400 mt-1`}>
          Overwrite a specific property value across all matching resources.
        </p>
      </div>

      <div className="space-y-4">
        {/* Property Name */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Property Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={propertyName}
            onChange={(e) => setPropertyName(e.target.value)}
            placeholder="division_id"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* New Value */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            New Value <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="12345678-1234-1234-1234-123456789abc"
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          />
        </div>

        {/* Create If Missing */}
        <div className={`flex items-center ${ui.gap.sm}`}>
          <input
            type="checkbox"
            id="createIfMissing"
            checked={createIfMissing}
            onChange={(e) => setCreateIfMissing(e.target.checked)}
            className={`${ui.svg.sm} text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-400`}
          />
          <label htmlFor="createIfMissing" className={`${ui.text.label} dark:text-gray-300`}>
            Create property if it doesn't exist
          </label>
        </div>

        {/* Expected Type (Optional) */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Expected Type (Optional)
          </label>
          <select
            value={expectedType}
            onChange={(e) => setExpectedType(e.target.value)}
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">None</option>
            <option value="string">String</option>
            <option value="int">Integer</option>
            <option value="bool">Boolean</option>
            <option value="double">Double</option>
            <option value="array">Array</option>
            <option value="object">Object</option>
            <option value="date">Date</option>
          </select>
        </div>

        {/* Expected Format (Optional) */}
        <div>
          <label className={`block ${ui.text.label} font-medium text-gray-700 dark:text-gray-300 mb-2`}>
            Expected Format (Optional)
          </label>
          <select
            value={expectedFormat}
            onChange={(e) => setExpectedFormat(e.target.value)}
            className={`w-full ${ui.input.base} border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="">None</option>
            <option value="email">Email</option>
            <option value="url">URL</option>
            <option value="date-iso8601">ISO 8601 Date</option>
          </select>
          <p className={` ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-1`}>
            Or enter a custom regex pattern
          </p>
        </div>
      </div>
    </div>
  );
};

export default OverwritePropertyParameters;
